import type { UserActivityLevel } from '../services/user-activity.service';

export const ICON_PATHS = {
  user: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  login:
    'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3 3m0 0l-3 3m3-3H2.25',
  mail: 'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
  device:
    'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3',
  sms: 'M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  call: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  gift: 'M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z',
  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  warning:
    'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z',
} as const;

export type IconKey = keyof typeof ICON_PATHS;

interface TypeMeta {
  label: string;
  icon: IconKey;
  /** Classi del bollino icona (sfondo + colore) per il livello info. */
  tone: string;
}

/** Etichette e resa visiva dei tipi evento (allineati a USER_ACTIVITY_TYPES lato API). */
export const ACTIVITY_TYPES: Record<string, TypeMeta> = {
  registration: { label: 'Registrazione', icon: 'user', tone: 'bg-indigo-50 text-indigo-600' },
  login: { label: 'Accesso', icon: 'login', tone: 'bg-indigo-50 text-indigo-600' },
  login_failed: { label: 'Accesso fallito', icon: 'login', tone: 'bg-indigo-50 text-indigo-600' },
  email_confirmed: { label: 'Email confermata', icon: 'mail', tone: 'bg-violet-50 text-violet-600' },
  password_reset: { label: 'Password reimpostata', icon: 'login', tone: 'bg-indigo-50 text-indigo-600' },
  phone_verified: { label: 'Telefono', icon: 'device', tone: 'bg-teal-50 text-teal-600' },
  sms_sent: { label: 'SMS inviato', icon: 'sms', tone: 'bg-sky-50 text-sky-600' },
  sms_failed: { label: 'SMS fallito', icon: 'sms', tone: 'bg-sky-50 text-sky-600' },
  email_sent: { label: 'Email inviata', icon: 'mail', tone: 'bg-violet-50 text-violet-600' },
  email_failed: { label: 'Email fallita', icon: 'mail', tone: 'bg-violet-50 text-violet-600' },
  call_initiated: { label: 'Chiamata avviata', icon: 'call', tone: 'bg-emerald-50 text-emerald-600' },
  call_accepted: { label: 'Chiamata accettata', icon: 'call', tone: 'bg-emerald-50 text-emerald-600' },
  call_ended: { label: 'Chiamata terminata', icon: 'call', tone: 'bg-emerald-50 text-emerald-600' },
  call_failed: { label: 'Chiamata non riuscita', icon: 'call', tone: 'bg-emerald-50 text-emerald-600' },
  bonus_granted: { label: 'Bonus accreditato', icon: 'gift', tone: 'bg-amber-50 text-amber-600' },
  support_message: { label: 'Supporto', icon: 'sms', tone: 'bg-neutral-100 text-neutral-600' },
  admin_chat_viewed: { label: 'Chat consultata dallo staff', icon: 'eye', tone: 'bg-orange-50 text-orange-600' },
  error: { label: 'Errore', icon: 'warning', tone: 'bg-red-50 text-red-600' },
};

/** Gruppi per il filtro "tipo" (value = tipo API, '' = tutti). */
export const TYPE_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Tutti i tipi' },
  ...Object.entries(ACTIVITY_TYPES).map(([value, m]) => ({ value, label: m.label })),
];

export const LEVEL_FILTERS: Array<{ value: '' | UserActivityLevel; label: string }> = [
  { value: '', label: 'Tutti' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Avvisi' },
  { value: 'error', label: 'Errori' },
];

export function typeMeta(type: string): TypeMeta {
  return ACTIVITY_TYPES[type] ?? { label: type, icon: 'warning', tone: 'bg-neutral-100 text-neutral-600' };
}

export function formatActivityDate(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
